
import { defineComponent, ref, computed, watch, triggerRef, onMounted, getCurrentInstance } from 'vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
import { planService } from '../../services'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { getConnectionStatus } from '@/api/connectors/yClients'

interface ShiftData {
    id?: string
    date: string
    salon: string
    channel: string
    metrics: Record<string, number>
    indicators?: Record<string, number> | string
}

interface Salon {
    id: string
    name: string
}

interface Channel {
    id: string
    name: string
}

interface Plan {
    id: string
    branchIds?: string[]
    channelIds?: string[]
    tasks?: any[]
    dailyRevenuePlan?: Record<string, number>
    generalDailyRevenuePlan?: number
    [key: string]: any
}

export default defineComponent({
    name: 'ShiftDataSidebar',
    components: {
        BaseInputOutlined,
        CustomActivatorSelect,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        shiftData: {
            type: Object as () => ShiftData | null,
            default: null,
        },
        activePlan: {
            type: Object as () => Plan | null,
            default: null,
        },
        plans: {
            type: Array as () => Plan[],
            default: () => [],
        },
        existingShifts: {
            type: Array as () => any[],
            default: () => [],
        },
        currentAdministrator: {
            type: String,
            default: '',
        },
        currentMonth: {
            type: Number,
            default: new Date().getMonth() + 1,
        },
        currentYear: {
            type: Number,
            default: new Date().getFullYear(),
        },
        projectId: {
            type: String,
            required: true,
        },
    },
    emits: ['close', 'save', 'delete'],
    setup(props, { emit }) {
        const instance = getCurrentInstance()
        const store = instance?.proxy?.$store

        const shiftData = ref<ShiftData>({
            date: new Date().toLocaleDateString('ru-RU'),
            salon: '',
            channel: '',
            metrics: {},
        })

        const salonDisplayValue = ref('')
        const channelDisplayValue = ref('')
        const autofillLoading = ref(false)

        // Статус авторизации коннектора yClients
        const yClientsConnectorAuthorized = ref(false)

        // Проверка наличия подключения к yClients
        const hasYClientsConnection = computed(() => {
            if (!props.projectId || !store) return false
            try {
                const project = store.getters.projectById(props.projectId)
                if (!project || !project.connectors) return false
                const yClientsConnector = project.connectors.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )
                return !!yClientsConnector
            } catch (error) {
                console.warn('Failed to check yClients connection:', String(error))
                return false
            }
        })

        // Функция для проверки статуса авторизации коннектора yClients
        const checkYClientsAuthorization = async () => {
            if (!props.projectId || !store) {
                yClientsConnectorAuthorized.value = false
                return
            }

            try {
                const project = store.getters.projectById(props.projectId)
                if (!project || !project.connectors) {
                    yClientsConnectorAuthorized.value = false
                    return
                }

                const yClientsConnector = project.connectors.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (!yClientsConnector) {
                    yClientsConnectorAuthorized.value = false
                    return
                }

                const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)

                if (error || !data || data.status !== 'active') {
                    yClientsConnectorAuthorized.value = false
                    return
                }

                yClientsConnectorAuthorized.value = true
            } catch (error) {
                console.warn('Failed to check yClients authorization:', String(error))
                yClientsConnectorAuthorized.value = false
            }
        }

        // Проверка наличия задач с автозаполнением в плане
        const hasTasksWithAutofill = computed(() => {
            if (!planTasks.value || planTasks.value.length === 0) return false
            const tasksToAutofill = planTasks.value.filter((task: any) => {
                return task.taskNumber && task.autofillParams && task.autofillParams.function
            })
            return tasksToAutofill.length > 0
        })

        // Автозаполнение доступно только для первого филиала плана
        const canShowAutofillForBranch = computed(() => {
            const branchIds = currentPlan.value?.branchIds || []
            if (branchIds.length <= 1) return true
            const firstBranchId = branchIds[0]
            const selectedBranchId = shiftData.value.salon || firstBranchId
            return selectedBranchId === firstBranchId
        })

        const hasAutofill = (task: any) => {
            if (!task) return false
            const autofillParams = task.autofillParams || task.autofill_params
            if (!autofillParams) return false
            if (typeof autofillParams === 'string') {
                try {
                    const parsed = JSON.parse(autofillParams)
                    return !!parsed?.function
                } catch {
                    return false
                }
            }
            return !!autofillParams.function
        }

        // Computed свойства для получения данных из текущего плана
        const availableSalons = computed(() => {
            if (!currentPlan.value || !currentPlan.value.branchIds) {
                return []
            }
            return currentPlan.value.branchIds.map((branchId: string) => ({
                id: branchId,
                name: branchId, // Можно заменить на реальное название филиала
            }))
        })

        const availableChannels = computed(() => {
            if (!currentPlan.value || !currentPlan.value.channelIds) {
                return []
            }
            return currentPlan.value.channelIds.map((channelId: string) => ({
                id: channelId,
                name: channelId, // Можно заменить на реальное название канала
            }))
        })

        // Computed свойство для задач из текущего плана
        const planTasks = computed(() => {
            if (!currentPlan.value || !currentPlan.value.tasks) {
                return []
            }

            return currentPlan.value.tasks.map((task: any) => ({
                id: task.id,
                name: task.name,
                unit: task.unit || '',
                description: task.description || '',
                norms: task.norms || {},
                taskNumber: task.taskNumber || task.task_number,
                autofillParams: task.autofillParams || task.autofill_params,
            }))
        })

        // Computed свойство для определения используемого плана
        const currentPlan = computed((): Plan | null => {
            // Если есть ID смены - это редактирование, используем план из смены
            if (shiftData.value.id) {
                const shift = props.existingShifts.find((s: any) => s.id === shiftData.value.id)
                if (shift && shift.planId) {
                    return props.plans.find((plan: Plan) => plan.id === shift.planId) || props.activePlan
                }
            }
            // Если нет ID смены - это создание новой смены, используем активный план
            return props.activePlan
        })

        // Инициализация данных при открытии сайдбара
        watch(
            () => props.visible,
            async newVal => {
                if (newVal) {
                    // Проверяем авторизацию коннектора yClients
                    await checkYClientsAuthorization()

                    // Сначала инициализируем базовую структуру
                    if (props.shiftData) {
                        // Редактирование существующей смены
                        shiftData.value = { ...props.shiftData }
                    } else {
                        // Создание новой смены - сбрасываем к базовым значениям
                        const firstSalon =
                            availableSalons.value[0]?.id || currentPlan.value?.branchIds?.[0] || ''
                        const firstChannel =
                            availableChannels.value[0]?.id || currentPlan.value?.channelIds?.[0] || ''

                        shiftData.value = {
                            date: new Date().toISOString().split('T')[0], // Формат YYYY-MM-DD для input type="date"
                            salon: firstSalon,
                            channel: firstChannel,
                            metrics: {},
                        }
                    }

                    // Инициализируем метрики для всех задач из плана (только если их нет)
                    if (planTasks.value.length > 0) {
                        planTasks.value.forEach((task: any) => {
                            if (shiftData.value.metrics[task.id] === undefined) {
                                shiftData.value.metrics[task.id] = 0
                            }
                        })
                    }

                    // Если это редактирование смены, убеждаемся что indicators правильно парсятся
                    if (props.shiftData && props.shiftData.indicators) {
                        // Парсим indicators если они пришли как строка
                        if (typeof props.shiftData.indicators === 'string') {
                            try {
                                shiftData.value.metrics = JSON.parse(props.shiftData.indicators)
                            } catch (e) {
                                // Ошибка парсинга индикаторов
                            }
                        } else {
                            shiftData.value.metrics = { ...props.shiftData.indicators }
                        }
                    }

                    triggerRef(shiftData)

                    // Обновляем отображаемые значения из активного плана
                    const salon = availableSalons.value.find((s: Salon) => s.id === shiftData.value.salon)
                    const channel = availableChannels.value.find(
                        (c: Channel) => c.id === shiftData.value.channel
                    )
                    salonDisplayValue.value = salon?.name || ''
                    channelDisplayValue.value = channel?.name || ''
                }
            }
        )

        const updateSalon = (salonId: string) => {
            const salon = availableSalons.value.find((s: Salon) => s.id === salonId)
            if (salon) {
                shiftData.value.salon = salonId
                salonDisplayValue.value = salon.name
            }
        }

        const updateChannel = (channelId: string) => {
            const channel = availableChannels.value.find((c: Channel) => c.id === channelId)
            if (channel) {
                shiftData.value.channel = channelId
                channelDisplayValue.value = channel.name
            }
        }

        // Методы для работы с задачами
        const getTaskValue = (taskId: string) => {
            return shiftData.value.metrics[taskId] ?? 0
        }

        const sanitizeNumericInput = (str: string): string => {
            const sanitized = String(str ?? '').replace(/[^\d.]/g, '')
            const parts = sanitized.split('.')
            return parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : sanitized
        }

        const updateTaskValue = (taskId: string, value: any) => {
            const sanitized = sanitizeNumericInput(String(value ?? ''))
            if (sanitized === '' || sanitized === '.') {
                shiftData.value.metrics[taskId] = 0
            } else {
                const num = parseFloat(sanitized)
                if (!isNaN(num)) {
                    shiftData.value.metrics[taskId] = num
                }
            }
            triggerRef(shiftData)
        }

        const updateDailyRevenue = (value: any) => {
            const sanitized = sanitizeNumericInput(String(value ?? ''))
            shiftData.value.metrics.dailyRevenue = sanitized === '' || sanitized === '.' ? 0 : (parseFloat(sanitized) || 0)
            triggerRef(shiftData)
        }

        const onNumericKeydown = (e: KeyboardEvent) => {
            const key = e.key
            if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) return
            if (e.ctrlKey || e.metaKey) {
                if (['a', 'c', 'v', 'x'].includes(key.toLowerCase())) return
            }
            if (/[0-9]/.test(key)) return
            if (key === '.') {
                const input = e.target as HTMLInputElement
                if (input && !input.value.includes('.')) return
            }
            e.preventDefault()
        }

        const onNumericPaste = (
            e: ClipboardEvent,
            updateFn: (value: string) => void,
            currentValue: number
        ) => {
            e.preventDefault()
            const pasted = (e.clipboardData?.getData('text') || '').replace(/[^\d.]/g, '')
            const parts = pasted.split('.')
            const sanitized = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : pasted
            if (sanitized) {
                const input = e.target as HTMLInputElement
                const start = input?.selectionStart ?? 0
                const end = input?.selectionEnd ?? 0
                const currentStr = String(currentValue ?? '')
                const merged = currentStr.slice(0, start) + sanitized + currentStr.slice(end)
                updateFn(merged)
            }
        }

        const getTaskNorm = (task: any) => {
            if (!task.norms) {
                return null
            }

            // Проверяем, есть ли филиалы и каналы в плане
            const hasBranches = currentPlan.value?.branchIds && currentPlan.value.branchIds.length > 0
            const hasChannels = currentPlan.value?.channelIds && currentPlan.value.channelIds.length > 0
            const singleBranch = hasBranches && currentPlan.value.branchIds.length === 1
            const singleChannel = hasChannels && currentPlan.value.channelIds.length === 1

            // Если нет филиалов или только один филиал, И нет каналов или только один канал
            // то показываем норму
            if ((!hasBranches || singleBranch) && (!hasChannels || singleChannel)) {
                // Определяем какой салон и канал использовать
                const salon = singleBranch
                    ? currentPlan.value.branchIds[0]
                    : shiftData.value.salon || 'general'
                const channel = singleChannel
                    ? currentPlan.value.channelIds[0]
                    : shiftData.value.channel || 'general'

                // Ищем норму по салону и каналу
                return (
                    task.norms[salon]?.[channel] ||
                    task.norms['general']?.[channel] ||
                    task.norms[salon]?.['general'] ||
                    task.norms['general']?.['general'] ||
                    null
                )
            }

            // В остальных случаях (несколько филиалов и/или каналов) показываем норму только если выбраны конкретные
            if (!shiftData.value.salon || !shiftData.value.channel) {
                return null
            }

            // Ищем норму по салону и каналу: task.norms[salon][channel]
            return task.norms[shiftData.value.salon]?.[shiftData.value.channel] || null
        }

        const getDailyRevenuePlan = () => {
            const currentSalonKey = shiftData.value.salon ? shiftData.value.salon : 'general'
            return currentPlan.value.dailyRevenuePlan[currentSalonKey] || null
        }

        const handleAutofill = async () => {
            if (!shiftData.value.date) {
                return
            }

            // Получаем задачи из плана, которые поддерживают автозаполнение (имеют task_number и autofill_params)
            const tasksToAutofill = planTasks.value.filter((task: any) => {
                return task.taskNumber && task.autofillParams && task.autofillParams.function
            })

            if (tasksToAutofill.length === 0) {
                alert('В плане нет задач, поддерживающих автозаполнение')
                return
            }

            autofillLoading.value = true
            try {
                // При автозаполнении администратор — всегда текущий пользователь (не из фильтра)
                const administratorId = store?.getters?.myUser?.sub || props.currentAdministrator || ''

                // Форматируем дату в нужный формат (YYYY-MM-DD)
                const shiftDate = shiftData.value.date

                // Автозаполняем каждую поддерживаемую задачу
                const allMetrics: Record<string, number> = {}

                for (const task of tasksToAutofill) {
                    const taskId = String(task.id)
                    const autofillParams = task.autofillParams

                    if (!autofillParams || !autofillParams.function) {
                        continue
                    }

                    try {
                        // Формируем параметры для вызова функции БД
                        let params: any = {}

                        if (autofillParams.function === 'ba_task_conv_service_opz') {
                            // Для задач 19-22
                            params.taskNumber = autofillParams.taskNumber || task.taskNumber
                            // Услуги не указываем (любые услуги), но можно использовать из autofillParams если есть
                            params.visitedServices = autofillParams.visitedServices
                            params.bookedServices = autofillParams.bookedServices
                        } else if (autofillParams.function === 'ba_task_daily_record') {
                            // Для задач 6-7
                            params.variantNumber = autofillParams.variantNumber
                        } else if (autofillParams.function === 'ba_task_daily_sales_sum_for_admin') {
                            // Для задач 8-9
                            // Используем recordServiceList из autofillParams (для задачи 8 будет [], для задачи 9 может быть заполнен)
                            params.recordServiceList = autofillParams.recordServiceList || []
                        }
                        // Для остальных функций (ba_task_add_daily_revenue, ba_task_successful_att_percent,
                        // ba_task_daily_product_sales_sum_for_admin, ba_task_daily_avg_client_bill_for_admin,
                        // ba_task_daily_avg_client_bill_for_admin_records) параметры не требуются

                        const metrics = await planService.autofillShiftData(
                            props.projectId,
                            shiftDate,
                            administratorId,
                            taskId,
                            params,
                            currentPlan.value?.id
                        )

                        // Объединяем метрики
                        Object.assign(allMetrics, metrics)
                    } catch (error) {
                        console.error(`Error autofilling task ${taskId}:`, error)
                        // Продолжаем для остальных задач даже если одна не удалась
                    }
                }

                // Обновляем метрики в shiftData
                shiftData.value.metrics = {
                    ...shiftData.value.metrics,
                    ...allMetrics,
                }
                triggerRef(shiftData)
            } catch (error) {
                console.error('Error autofilling shift data:', error)
                alert('Ошибка при автозаполнении данных. Попробуйте еще раз.')
            } finally {
                autofillLoading.value = false
            }
        }

        const autofillData = () => {
            if (!currentPlan.value || !currentPlan.value.tasks) {
                // Автозаполнение данными по умолчанию, если нет активного плана
                const defaultMetrics: any = {}
                planTasks.value.forEach((task: any) => {
                    defaultMetrics[task.id] = 0
                })
                defaultMetrics.dailyRevenue = 70000
                shiftData.value.metrics = defaultMetrics
                return
            }

            // Автозаполнение данными из текущего плана
            const metrics: any = {}

            currentPlan.value.tasks.forEach((task: any) => {
                if (task.norms && shiftData.value.salon) {
                    metrics[task.id] = task.norms[shiftData.value.salon]?.general || 0
                } else {
                    metrics[task.id] = 0
                }
            })

            // Дневная выручка из плана
            if (currentPlan.value.dailyRevenuePlan && shiftData.value.salon) {
                metrics.dailyRevenue = currentPlan.value.dailyRevenuePlan[shiftData.value.salon] || 70000
            } else {
                metrics.dailyRevenue = 70000
            }

            shiftData.value.metrics = { ...shiftData.value.metrics, ...metrics }
        }

        const saveShift = () => {
            emit('save', shiftData.value)
        }

        const deleteShift = () => {
            emit('delete', shiftData.value)
        }

        const closeSidebar = () => {
            emit('close')
        }

        return {
            shiftData,
            salonDisplayValue,
            channelDisplayValue,
            availableSalons,
            availableChannels,
            planTasks,
            updateSalon,
            updateChannel,
            getTaskValue,
            updateTaskValue,
            updateDailyRevenue,
            onNumericKeydown,
            onNumericPaste,
            getTaskNorm,
            getDailyRevenuePlan,
            hasYClientsConnection,
            hasTasksWithAutofill,
            canShowAutofillForBranch,
            hasAutofill,
            yClientsConnectorAuthorized,
            autofillData,
            autofillLoading,
            handleAutofill,
            saveShift,
            deleteShift,
            closeSidebar,
        }
    },
})
